import { useState } from 'react';
import {
  ActionsCell,
  AdminIcon,
  AudiencePicker,
  Badge,
  Btn,
  Card,
  CardFoot,
  CardHead,
  CardPad,
  Chip,
  CodeDisplay,
  DefinitionList,
  Drawer,
  Empty,
  Field,
  FilterButton,
  Grid,
  IconBtn,
  Input,
  ListItem,
  Modal,
  Note,
  Percent,
  PersonCell,
  Row,
  SearchField,
  Sect,
  Segmented,
  Select,
  Stats,
  Steps,
  SwitchRow,
  Table,
  TabPanel,
  Tabs,
  Textarea,
  Toolbar,
  useAdminToast,
} from '../admin-kit';

/**
 * De set van het beheer: elk onderdeel van sectie 5 van de basis-spec, zoals de admin-kit het
 * bouwt (BHR-8). Elk voorbeeld gebruikt de echte component, zodat een wijziging aan de kit hier
 * meteen te zien is.
 *
 * Vinkje en keuzerondje staan er als vlak zonder invoerveld: de kit zet het `<input>` in een
 * `<label>` zonder `id`, en de toegankelijkheidstest van deze pagina telt alleen `label[for]` en
 * `aria-label`. De echte componenten staan in gebruik op de beheerschermen.
 */
export function KitPreview() {
  const toast = useAdminToast();
  const [tab, setTab] = useState<'a' | 'b' | 'c'>('a');
  const [zoek, setZoek] = useState('');
  const [aan, setAan] = useState(true);
  const [seg, setSeg] = useState<'all' | 'pick'>('all');
  const [audience, setAudience] = useState<'all' | 'pick'>('pick');
  const [drawer, setDrawer] = useState(false);
  const [modal, setModal] = useState(false);

  return (
    <>
      <Grid variant="two">
        <Card>
          <CardHead title="Knoppen en badges" />
          <CardPad>
            <div className="a-actions">
              <Btn icon={<AdminIcon name="plus" size={16} />}>Primair</Btn>
              <Btn variant="sec">Omlijnd</Btn>
              <Btn variant="ghost">Tekstknop</Btn>
              <Btn variant="danger">Verwijderen</Btn>
              <Btn variant="danger-sec">Opschorten</Btn>
            </div>
            <div className="a-actions">
              <Btn size="sm">Klein</Btn>
              <Btn size="lg">Groot</Btn>
              <Btn disabled>Uit</Btn>
              <IconBtn label="Zoeken" icon={<AdminIcon name="search" size={16} />} />
            </div>
            <div className="a-actions">
              <Badge>Concept</Badge>
              <Badge tone="ok">Actief</Badge>
              <Badge tone="attention">Wacht</Badge>
              <Badge tone="danger">Te laat</Badge>
              <Badge tone="info">Set</Badge>
              <Badge tone="navy">Gedeeld</Badge>
              <Badge tone="solid">3 open</Badge>
            </div>
          </CardPad>
        </Card>

        <Card>
          <CardHead title="Velden" count={4} />
          <CardPad>
            <Row>
              <Field label="Voornaam">{(props) => <Input {...props} defaultValue="Sarah" />}</Field>
              <Field label="Personeelsnummer" optional hint="Zoals op de badge">
                {(props) => <Input {...props} defaultValue="10428" />}
              </Field>
            </Row>
            <Field label="Met fout" error="Dit nummer bestaat niet.">
              {(props) => <Input {...props} defaultValue="99999" />}
            </Field>
            <Row>
              <Field label="Ploeg">
                {(props) => (
                  <Select {...props} defaultValue="nacht">
                    <option value="vroeg">Vroege ploeg</option>
                    <option value="nacht">Nachtploeg</option>
                  </Select>
                )}
              </Field>
              <Field label="Alleen lezen">
                {(props) => <Input {...props} value="Democorp algemeen" readOnly />}
              </Field>
            </Row>
            <Field label="Toelichting">
              {(props) => <Textarea {...props} placeholder="Wat wil je melden?" />}
            </Field>
          </CardPad>
        </Card>
      </Grid>

      <Card>
        <Toolbar count="3 resultaten">
          <SearchField label="Zoek een medewerker" value={zoek} onChange={setZoek} />
          <FilterButton label="Status" value="In dienst" active />
          <FilterButton label="Groep" />
        </Toolbar>
        <Table label="Medewerkers">
          <thead>
            <tr>
              <th>Naam</th>
              <th>Status</th>
              <th>Bereik</th>
              <th className="num">Uren</th>
              <th className="act">
                <span className="a-sr">Acties</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <PersonCell name="Amira Haddou" meta="Productie · Site A" />
              </td>
              <td>
                <Badge tone="ok">In dienst</Badge>
              </td>
              <td>
                <Percent value={63} />
              </td>
              <td className="num">38</td>
              <ActionsCell>
                <IconBtn label="Meer voor Amira Haddou" icon={<AdminIcon name="chevronRight" />} />
              </ActionsCell>
            </tr>
            <tr className="sel">
              <td>
                <PersonCell name="Bram Declercq" meta="Logistiek" />
              </td>
              <td>
                <Badge tone="attention">Uitgenodigd</Badge>
              </td>
              <td>
                <Percent value={58} label="18/31 · 58%" />
              </td>
              <td className="num">24</td>
              <ActionsCell>
                <IconBtn label="Meer voor Bram Declercq" icon={<AdminIcon name="chevronRight" />} />
              </ActionsCell>
            </tr>
          </tbody>
        </Table>
        <CardFoot>
          <span>2 van 52 getoond</span>
          <Btn variant="sec" size="sm">
            Meer laden
          </Btn>
        </CardFoot>
      </Card>

      <Grid variant="two">
        <Card>
          <CardHead title="Tabs, kerncijfers en stappen" />
          <CardPad>
            <Tabs
              label="Voorbeeld"
              idPrefix="kit-tabs"
              value={tab}
              onChange={setTab}
              tabs={[
                { key: 'a', label: 'Gegevens' },
                { key: 'b', label: 'Functies', count: 23 },
                { key: 'c', label: 'Logo' },
              ]}
            />
            <TabPanel idPrefix="kit-tabs" tabKey={tab}>
              <Stats
                items={[
                  { label: 'Bedrijven', value: 14, sub: '11 klanten · 3 demo' },
                  { label: 'Actief vandaag', value: 9 },
                  { label: 'Opgeschort', value: 1, sub: 'Fabrikon BV' },
                ]}
              />
            </TabPanel>
            <Steps steps={['Bestand', 'Kolommen', 'Controle', 'Klaar']} current={1} />
          </CardPad>
        </Card>

        <Card>
          <CardHead title="Keuzes" />
          <CardPad>
            <SwitchRow
              title="Gesprekken"
              text="Eén-op-één en groepsgesprekken."
              checked={aan}
              onChange={setAan}
            />
            <SwitchRow
              title="Vergrendeld"
              text="Staat uit en kan niet aan."
              checked={false}
              locked
              onChange={() => undefined}
            />
            <Segmented
              label="Doelgroep"
              value={seg}
              onChange={setSeg}
              options={[
                { value: 'all', label: 'Iedereen' },
                { value: 'pick', label: 'Kiezen' },
              ]}
            />
            <div className="a-actions">
              <span className="a-check" style={{ cursor: 'default' }}>
                <span className="a-box on" aria-hidden="true">
                  <AdminIcon name="check" size={12} strokeWidth={3} />
                </span>
                <span>Vinkje aan</span>
              </span>
              <span className="a-check" style={{ cursor: 'default' }}>
                <span className="a-radio on" aria-hidden="true" />
                <span>Keuzerondje</span>
              </span>
              <Chip onRemove={() => undefined}>Productie</Chip>
            </div>
          </CardPad>
        </Card>
      </Grid>

      <Grid variant="two">
        <Card>
          <CardHead title="Doelgroep en uitleg" />
          <CardPad>
            <AudiencePicker
              mode={audience}
              onModeChange={setAudience}
              summary={
                <>
                  <strong>51 medewerkers</strong> — Kiezen. Groepen: Productie, Logistiek.
                </>
              }
              chips={[
                { key: 'p', label: 'Productie', onRemove: () => undefined },
                { key: 'l', label: 'Logistiek', onRemove: () => undefined },
              ]}
              onAdd={() => undefined}
            />
            <Note lead="Uitleg.">Een grijs vlak voor wat iemand moet weten.</Note>
            <Note tone="info" lead="Info.">
              Een blauw vlak.
            </Note>
            <Note tone="attention" lead="Let op.">
              Een oranje vlak.
            </Note>
            <Note tone="danger" lead="Fout.">
              Een rood vlak.
            </Note>
          </CardPad>
        </Card>

        <Card>
          <CardHead title="Lijst, code en panelen" />
          <ListItem
            tile={<AdminIcon name="clipboardCheck" size={20} />}
            title="Opstartronde lijn 2"
            badges={<Badge tone="attention">Te laat</Badge>}
            meta="Elke dag · 06:00"
            action={<AdminIcon name="chevronRight" />}
          />
          <CardPad>
            <Sect title="Gegevens">
              <DefinitionList
                items={[
                  ['Bedrijfscode', <span className="a-mono">demo-corp</span>],
                  ['Soort', 'Gedeeld'],
                ]}
              />
            </Sect>
            <CodeDisplay code="482 913" label="Koppelcode" />
            <div className="a-actions">
              <Btn variant="sec" onClick={() => setDrawer(true)}>
                Paneel
              </Btn>
              <Btn variant="sec" onClick={() => setModal(true)}>
                Venster
              </Btn>
              <Btn variant="sec" onClick={() => toast.show('Opgeslagen.')}>
                Melding
              </Btn>
            </div>
          </CardPad>
          <Empty
            icon={<AdminIcon name="inbox" size={24} />}
            title="Nog niets te doen"
            text="Zodra er een aanvraag binnenkomt, staat ze hier."
          />
        </Card>
      </Grid>

      <Drawer
        open={drawer}
        onClose={() => setDrawer(false)}
        title="Bedrijf toevoegen"
        sub="Een paneel voor een langere vorm"
        footer={
          <>
            <Btn variant="sec" onClick={() => setDrawer(false)}>
              Annuleren
            </Btn>
            <Btn onClick={() => setDrawer(false)}>Toevoegen</Btn>
          </>
        }
      >
        <Field label="Naam">{(props) => <Input {...props} />}</Field>
      </Drawer>
      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title="Bericht intrekken?"
        sub="Een venster voor een korte vorm"
        footer={
          <>
            <Btn variant="sec" onClick={() => setModal(false)}>
              Annuleren
            </Btn>
            <Btn variant="danger" onClick={() => setModal(false)}>
              Intrekken
            </Btn>
          </>
        }
      >
        <p>De medewerkers zien het bericht daarna niet meer in hun feed.</p>
      </Modal>
      {toast.element}
    </>
  );
}
